<template>
	<MStateBox :loading="news_detail.loading" :empty="isEmpty" class="new-detail-container" v-if="initialized">
		<div class="new-detail-title">{{ detailData.title }}</div>
		<div class="new-detail-date">
			{{ $t('enterprise.data-time') + '：' + formatDateStamp(detailData.time) }}
		</div>
		<div v-if="detailData.content" v-html="detailData.content" class="new-detail-content jm-editor"></div>

		<template v-if="!isFrame">
			<div class="new-detail-slide new-detail-line">
				<div>{{ $t('enterprise.news-updated') }} ：</div>
				<div v-if="isExternalLink">{{ $t('enterprise.news-resource') }}：</div>
			</div>
			<div class="new-detail-value new-detail-line">
				<div class="repair">
					{{ detailData.updateTime }}
				</div>
				<div v-if="isExternalLink" class="underline word-wrap" @click="copyLink(detailData.url)">
					{{ detailData.url }}
				</div>
			</div>
		</template>
	</MStateBox>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, watch } from 'vue';
import { dateFormat } from '@/utils/api';
import MStateBox from '@/view/enterprise_rebuild/components/MStateBox.vue';
import { useRoute } from '@/utils/vue-api';
import i18n from '@/assets/js/i18n';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';

// 使用rebuild数据hooks
const { news_detail } = useModuleData();

const route = useRoute();
const { proxy } = getCurrentInstance();

// 响应式数据
const storeId = route.query.storeId;
const newId = route.query.newsId || route.query.newId; // newId 历史链接参数兼容
const isFrame = route.query.isFrame;
const initialized = ref(false);

// 计算属性 - 保持与历史组件数据访问路径一致
const detailData = computed(() => {
	return news_detail.data?.data || {}; // 历史组件访问路径：res.data
});

const isEmpty = computed(() => {
	return news_detail.empty || !detailData.value || Object.keys(detailData.value).length === 0;
});

// 自由新闻没有数据来源
// irm算自有的，其他的是爬取的
const isExternalLink = computed(() => {
	if (!detailData.value || !detailData.value.sourceType) return false;
	// sourceType(源头类型,1-IRM,2-官网,3-微信公众号)
	return detailData.value.sourceType !== 1;
});

function loadDetail() {
	if (!storeId || !newId) {
		// 设置空状态
		news_detail.empty = true;
		console.log('---- 设置空状态');
		return;
	}

	// 使用rebuild数据hooks加载详情
	news_detail.refresh({
		storeId: storeId,
		newsId: newId
	});
}

function formatDateStamp(date, type = 'yyyy-MM-dd') {
	return dateFormat(new Date(Number(date)), type);
}

function copyLink(link) {
	proxy.$copyText(link).then(
		e => {
			proxy.showTooltip({ msg: i18n.t('enterprise.copy-success'), time: 2000 });
		},
		e => {
			proxy.showTooltip({ msg: i18n.t('enterprise.copy-error'), time: 2000 });
		}
	);
}

function postMsgEvent(e) {
	if (typeof e.data == 'string') {
		console.log('---- 收到 msg', e.data);
		// 直接设置数据，绕过hooks
		news_detail.data = { data: JSON.parse(e.data) };
		news_detail.loading = false;
	}
}

onMounted(async () => {
	if (isFrame) {
		window.addEventListener('message', postMsgEvent);
	} else {
		await loadDetail();
	}
	initialized.value = true;
});

onBeforeUnmount(() => {
	if (isFrame) {
		document.removeEventListener('message', postMsgEvent);
	}
});
</script>

<style lang="less" scoped>
.new-detail-container {
	padding: 0.12rem 0.14rem 0.5rem 0.14rem;
	min-height: 100vh;
	background: #fff;
	.new-detail-title {
		font-family: 'PingFang SC';
		font-weight: 500;
		font-size: 0.22rem;
		line-height: 140%;
		color: #1d2129;
	}
	.new-detail-date {
		margin-top: 0.1rem;
		font-family: 'Repair-Regular';
		font-size: 0.12rem;
		line-height: 0.16rem;
		color: #a5a5a5;
	}

	.new-detail-content {
		margin-top: 0.16rem;
		font-size: 0.16rem;
		line-height: 200%;
		color: #333333;
		overflow: hidden;
	}
	// 富文本
	/deep/ .jm-editor {
		white-space: normal !important;

		h1 {
			font-size: 32px;
			font-weight: bold;
		}

		h2 {
			font-size: 24px;
			font-weight: bold;
		}

		h3 {
			font-size: 18.72px;
			font-weight: bold;
		}

		h4 {
			font-size: 16px;
			font-weight: bold;
		}

		h5 {
			font-size: 13.28px;
		}

		h6 {
			font-size: 12px;
		}

		p {
			font-size: 0.14rem;
			margin-bottom: 0.1rem !important;
			margin-top: 0 !important;
			line-height: 200%;

			&:first-child {
				margin-top: 0 !important;
			}

			&:last-child {
				margin-bottom: 0 !important;
			}
			&:has(img) {
				line-height: 1;
			}
		}
	}

	.new-detail-line {
		display: inline-block;
		font-size: 0.12rem;
		line-height: 0.16rem;
		color: #a5a5a5;

		div + div {
			margin-top: 0.08rem;
		}
		.repair {
			font-family: 'Repair-Regular';
		}
		.underline {
			text-decoration: underline;
		}
		.word-wrap {
			word-break: break-all;
		}
	}
	.new-detail-slide {
		margin-top: 0.16rem;
	}
	.new-detail-value {
		margin-top: 0.16rem;
		margin-left: 0.06rem;
	}
}
</style>
